import React from 'react'
import { InlineLink } from '@primer/react-brand'

import Header from '../components/Header'
import Footer from '../components/Footer'

// Civic Lens long-form legal pages — Terms of Service and Privacy Policy.
// One component renders either document (selected by the `doc` prop); CivicLensApp
// maps the `terms` and `privacy` page keys onto it. Reached from the footer's Legal
// column and the sign-up fine print, and served at /terms and /privacy on the
// Civic Lens domain (MainController#terms / #privacy).
//
// Design: "Legal Pages.html" / legal-pages.jsx in the v2 design handoff. The two
// docs are standalone pages cross-linked via inline links and the footer (the
// design's .lg-tab subnav is defined in CSS but unused, so no in-page tab switcher).
//
// Color: the design's hex values map onto the @primer/react-brand base color scale
// (see docs/design-tokens.md): #424a53 → gray-7 (body), #57606a → gray-6, #8a929c →
// gray-4 (date, nearest step), #24292f/#1f2328 → gray-9 (headings; the title's
// #1f2328 is a touch darker — gray-9 is the nearest step), #eef0f2 → gray-1
// (dividers), #0969da → InlineLink's brand accent (blue-5). The decorative list
// markers (#9aa3ad dot, #c3cdd6 dash) use gray-4 / gray-3 as nearest steps. No
// hardcoded hex. Links use InlineLink (in-sentence Primer Brand link: inherits the
// surrounding type, no arrow); the design's dropped/placeholder links are restored
// to their real production URLs per Mischa's call.

const C = {
  gray9: 'var(--base-color-scale-gray-9)',
  gray7: 'var(--base-color-scale-gray-7)',
  gray6: 'var(--base-color-scale-gray-6)',
  gray4: 'var(--base-color-scale-gray-4)',
  gray3: 'var(--base-color-scale-gray-3)',
  gray1: 'var(--base-color-scale-gray-1)',
}

const Title = ({ children }) => (
  <h1 style={{ margin: 0, fontSize: 40, fontWeight: 700, letterSpacing: '-0.028em', lineHeight: 1.08, color: C.gray9 }}>
    {children}
  </h1>
)

const Updated = ({ children }) => (
  <p style={{ margin: '16px 0 0', fontSize: 13.5, fontWeight: 500, color: C.gray4 }}>{children}</p>
)

const Intro = ({ children }) => (
  <p style={{ margin: '30px 0 0', fontSize: 17.5, lineHeight: 1.7, color: C.gray7 }}>{children}</p>
)

const Section = ({ first = false, title, children }) => (
  <section
    style={{
      marginTop: first ? 30 : 44,
      paddingTop: first ? 0 : 44,
      borderTop: first ? 'none' : `1px solid ${C.gray1}`,
    }}
  >
    <h2 style={{ margin: '0 0 18px', fontSize: 23, fontWeight: 700, letterSpacing: '-0.015em', color: C.gray9 }}>
      {title}
    </h2>
    {children}
  </section>
)

const P = ({ lead = false, children }) => (
  <p style={{ margin: lead ? '0 0 14px' : '0 0 16px', fontSize: 16, lineHeight: 1.75, color: C.gray7 }}>{children}</p>
)

const List = ({ children }) => (
  <ul style={{ margin: '0 0 16px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 13 }}>
    {children}
  </ul>
)

const LI = ({ children }) => (
  <li style={{ position: 'relative', paddingLeft: 22, fontSize: 16, lineHeight: 1.7, color: C.gray7 }}>
    <span style={{ position: 'absolute', left: 3, top: 10, width: 5, height: 5, borderRadius: '50%', background: C.gray4 }} />
    {children}
  </li>
)

const SubList = ({ children }) => (
  <ul style={{ margin: '13px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 11 }}>
    {children}
  </ul>
)

const SubLI = ({ children }) => (
  <li style={{ position: 'relative', paddingLeft: 20, fontSize: 15.5, lineHeight: 1.65, color: C.gray6 }}>
    <span style={{ position: 'absolute', left: 2, top: 11, width: 9, height: 1.5, borderRadius: 2, background: C.gray3 }} />
    {children}
  </li>
)

const Addr = ({ children }) => (
  <address style={{ margin: '6px 0 16px', fontSize: 16, lineHeight: 1.7, fontStyle: 'normal', fontWeight: 500, color: C.gray9 }}>
    {children}
  </address>
)

const B = ({ children }) => <b style={{ color: C.gray9, fontWeight: 600 }}>{children}</b>

// In-sentence link. `external` opens in a new tab (used for third-party / outside
// URLs); internal (/terms, /privacy) and mailto links stay in-tab.
const A = ({ href, external = false, children }) => (
  <InlineLink href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
    {children}
  </InlineLink>
)

const TermsContent = () => (
  <>
    <Title>CivicLens Terms of Service</Title>
    <Updated>Last updated: August 27, 2023</Updated>
    <Intro>
      By using CivicLens, the user agrees to comply with these Terms of Service. The Terms of Service is the agreement
      between the user and CivicLens and governs how the user can use CivicLens. This agreement supersedes any previous
      agreements between CivicLens and the user.
    </Intro>

    <Section title="Introduction">
      <P>
        CivicLens is a platform allowing users to log instances of problematic content posted online such as mis- and
        dis-information, create an archive of these instances and undertake research, analysis and monitoring of trends
        in the spread of this content.
      </P>
      <P>
        Use of CivicLens is subject to these Terms of Use (“Terms”), which govern how you may use CivicLens and form
        the agreement between the user (“you”, “user”) and Algorithmic Transparency Institute (“ATI”, “we”, “us”, “our”)
        a project of the <A href="https://ncoc.org/" external>National Conference on Citizenship</A>.
      </P>
      <P>
        ATI grants you a non-transferable, non-exclusive, revokable, limited to use and access CivicLens. We reserve all
        rights not expressly granted to you by these Terms.
      </P>
      <P>
        If you violate any of these Terms, then ATI may terminate your account. ATI's failure to enforce or exercise any
        of these Terms does not constitute a waiver.
      </P>
      <P>These Terms are to be read in conjunction with any other agreement that may be in place between ATI and you.</P>
      <P>ATI may modify these Terms from time to time and will publish any modifications here.</P>
    </Section>

    <Section title="Account and usage">
      <List>
        <LI>
          Users must provide ATI with their full legal name, correct email address, and other information requested in
          order to be authorized and create an account to use CivicLens. This information will be processed in
          accordance with our <A href="/privacy">Privacy Policy</A>.
        </LI>
        <LI>
          You may use CivicLens to upload instances of problematic content posted online such as mis- and dis-information
          (“Content”) and access Content on CivicLens for the purposes of public interest archiving, research and/or
          journalism (“Specified Purposes”) only.
        </LI>
        <LI>Users will be granted access to CivicLens and its Content for the Specified Purposes only.</LI>
        <LI>
          You may not use CivicLens for any purpose that is unlawful, fraudulent, or in any way that has any unlawful or
          fraudulent effect.
        </LI>
        <LI>Users cannot use CivicLens to violate laws or for any illegal activity in the user's jurisdiction.</LI>
        <LI>A user cannot be a “bot” or other automated being.</LI>
        <LI>Users are responsible for any activity or content posted through their account.</LI>
        <LI>
          Users are responsible for ensuring that their account and password are kept secure, including by using a strong
          password. ATI will not be held responsible for any damage or loss caused by a user's failure to secure their
          account.
        </LI>
        <LI>
          If users provide suggestions or feedback regarding CivicLens, then ATI may use these to improve its services.
          In such situations, the user gives ATI a transferable, royalty-free, and unlimited license to use such
          suggestions or feedback without a requirement to give the user any compensation.
        </LI>
      </List>
    </Section>

    <Section title="Content">
      <List>
        <LI>Users may only upload Content to CivicLens in good faith and for the Specified Purposes.</LI>
        <LI>
          You may not upload Content that breaches these Terms or any applicable local, national or international law or
          regulation.
        </LI>
        <LI>ATI retain the right to review and approve or deny any Content you upload to CivicLens.</LI>
        <LI>
          ATI may contact you to verify details or gather further information about Content uploaded through your account.
        </LI>
        <LI>
          When you submit Content to CivicLens, you grant ATI a worldwide, royalty-free, perpetual, irrevocable,
          non-exclusive, transferable, and sublicensable license to use, copy, modify, adapt, prepare derivative works
          of, distribute, store, display, make copies of and share that Content with other CivicLens users and third
          parties. This license is required to enable ATI to maintain CivicLens and make content available for other
          users for the Specified Purposes.
        </LI>
        <LI>
          Uploaded Content is only visible to other users who are in the same network as the user that uploaded the
          Content.
        </LI>
        <LI>
          Users can specify that uploaded content is only visible to their own organization by checking the appropriate
          field for each Content item. This can be made the default in preference settings for their organization.
        </LI>
        <LI>All Content remains visible to CivicLens, regardless of sharing settings.</LI>
        <LI>
          You may not use CivicLens in order to threaten, harass, intimidate, humiliate any person, incite violence or
          hate based on identity or vulnerability.
        </LI>
        <LI>
          While we endeavor to host Content uploaded to the platform in compliance with these Terms, we do not make any
          guarantees that we will do so.
        </LI>
        <LI>
          To report any issues about Content on CivicLens, contact{' '}
          <A href="mailto:support@civiclens.dev">support@civiclens.dev</A>.
        </LI>
      </List>
    </Section>

    <Section title="Sharing Content">
      <P lead>
        The publication or public sharing of any Content by a user is prohibited, except where all of the following
        conditions are met:
      </P>
      <SubList>
        <SubLI>
          user must obtain permission from the CivicLens user who submitted the Content, if the user is able to identify
          and contact the original user, they may be contacted directly, otherwise email{' '}
          <A href="mailto:support@civiclens.dev">support@civiclens.dev</A>;
        </SubLI>
        <SubLI>
          prior to publication or public sharing, user must protect personal data in the Content to the fullest extent
          possible, for example by redacting names, locations and other information that may allow for the identification
          of individuals, taking into account whether the Content was initially posted publicly or privately (e.g. within
          a closed group);
        </SubLI>
        <SubLI>
          user must exercise responsible communication strategies to avoid amplifying problematic themes in Content,
          including appropriately watermarking images or videos that are false or misleading and ensuring that headlines
          and information included in publications does not exacerbate the issues raised by the Content; and
        </SubLI>
        <SubLI>
          user must credit sources where appropriate, for example by referencing CivicLens and/or the user who submitted
          the Content.
        </SubLI>
      </SubList>
      <P lead>
        <span style={{ display: 'block', marginTop: 20 }}>
          Where a user wants to privately share Content from CivicLens with a third party, the user must first:
        </span>
      </P>
      <SubList>
        <SubLI>
          notify and obtain ATI's permission by emailing{' '}
          <A href="mailto:support@civiclens.dev">support@civiclens.dev</A>; and
        </SubLI>
        <SubLI>engage the third party in writing, on terms that replicate the obligations set out in these Terms.</SubLI>
      </SubList>
    </Section>

    <Section title="Tiplines">
      <P>
        Users may operate Tiplines – SMS, WhatsApp, email, webform, Slack integration, API connection or other submission
        forms where instances of mis- and dis-information can be submitted by members of the public.
      </P>
      <P>
        Users can generate and manage Tiplines through their CivicLens accounts by navigating to the drop down menu
        under their profile and then to the ‘Manage Organization’ section.
      </P>
    </Section>

    <Section title="Data protection">
      <P>
        Where a user shares, publishes, downloads or otherwise deals with Content on CivicLens, user is responsible for
        complying with all applicable local, state, national and international data protection laws including the EU
        General Data Protection Regulation (“GDPR”), together the “Data Protection Laws”.
      </P>
      <P>
        If a user receives an access, deletion or other request or enquiry about CivicLens from an individual, data
        protection supervisory authority, social media platform or other body, the user must inform ATI immediately
        before responding to the request. ATI and the user agree to assist each other in responding to any such requests.
      </P>
      <P>
        To the extent the relationship between user and CivicLens is required to be defined under Data Protection Laws,
        user and CivicLens are independent data controllers, both independently responsible for complying with the
        applicable obligations on them in Data Protection Laws.
      </P>
      <P lead>
        If (i) the Content you upload to CivicLens includes any personal data that is protected under GDPR (i.e. the
        personal data of data subjects in the United Kingdom or European Union); and (ii) CivicLens processes that data
        outside of the European Economic Area and United Kingdom; and (iii) such processing takes place in a country that
        that the European Commission (or competent United Kingdom authority, as applicable) has not provided an adequacy
        decision for, then CivicLens shall be deemed to have entered into the Controller to Controller Standard
        Contractual Clauses 2010 (Commission Decision 2004/915/EC) (“C2C SCCs”) with you, the terms of which (excluding
        any optional clauses) are hereby incorporated into these Terms by reference, on the following basis:
      </P>
      <SubList>
        <SubLI>you are the data exporter and CivicLens is the data importer;</SubLI>
        <SubLI>the governing law of the C2C SCCs is English law;</SubLI>
        <SubLI>
          for the purpose of Annex B to the C2C SCCs (i) the data subjects are those individuals whose personal data is
          contained in the data provided to CivicLens in accordance with these Terms and includes both submitters and
          posters of Content; (ii) categories of data are personal data included in any Content you submit to CivicLens,
          including individuals' social media handles, names and other personal data included in Content; (iii) the
          categories of sensitive data may include political and philosophical beliefs; (iv) the basic processing
          activities are the uses of data by CivicLens as set forth in these Terms.
        </SubLI>
      </SubList>
    </Section>

    <Section title="Intellectual property">
      <P>
        CivicLens aggregates Content from third parties. The copyright, ownership and other intellectual property rights
        in Content on CivicLens is vested in the author or owner of such Content. Accordingly, CivicLens does not grant
        users a license to this Content.
      </P>
      <P>CivicLens makes the Content on the platform available solely for the Specified Purposes.</P>
    </Section>

    <Section title="Application Program Interface (“API”) Terms">
      <P lead>
        The user may access CivicLens through the API. The use of the API requires compliance with these Terms as well
        as the following API Terms:
      </P>
      <List>
        <LI>
          The user agrees and understands that CivicLens will not be responsible for any loss or damage that results
          from their API usage or by products by third-parties that acquire data through the API.
        </LI>
        <LI>
          Excessive requests to or abuse of CivicLens through the API can lead to the suspension, whether temporary or
          permanent, of the account's ability to use the API. ATI will decide whether there has been an excessive usage
          or abuse of the CivicLens API and will issue an email to the user about the suspension prior to its occurrence
          as a warning.
        </LI>
      </List>
    </Section>

    <Section title="Third Party Services">
      <List>
        <LI>
          <B>YouTube:</B> some CivicLens features integrate with YouTube and the YouTube API. By using CivicLens, you
          agree to be bound by the{' '}
          <A href="https://www.youtube.com/static?template=terms" external>YouTube Terms of Service</A> and the{' '}
          <A href="https://policies.google.com/privacy?hl=en-US" external>Google Privacy Policy</A>.
        </LI>
        <LI>
          <B>CrowdTangle:</B> some CivicLens features integrate with CrowdTangle and the CrowdTangle API. By using Civic
          Lens, you agree to be bound by the{' '}
          <A href="https://www.crowdtangle.com/terms" external>CrowdTangle Terms of Service</A>.
        </LI>
      </List>
    </Section>

    <Section title="Account closure, termination and deletion of Content">
      <List>
        <LI>
          Users may delete their accounts at any time by contacting{' '}
          <A href="mailto:support@civiclens.dev">support@civiclens.dev</A>.
        </LI>
        <LI>
          When a user account is deleted, the personal data associated with the account will be deleted. Any Content
          submitted through the account will remain on the CivicLens platform in accordance with these Terms.
        </LI>
        <LI>
          ATI has the right to suspend or terminate the user's account at any time for a violation of these Terms or for
          any other reason and prohibit the user from using the account in the future.
        </LI>
      </List>
    </Section>

    <Section title="Modifications">
      <P>
        ATI will not be held responsible by the user or a third party for any change to or discontinuation of CivicLens.
      </P>
    </Section>

    <Section title="General Provisions">
      <List>
        <LI>The user accesses CivicLens at their own risk.</LI>
        <LI>
          The user cannot claim to be associated with or reference CivicLens or ATI except in compliance with these
          Terms and with ATI's permission.
        </LI>
        <LI>The user cannot adapt, hack, or modify CivicLens.</LI>
        <LI>The user cannot exploit, copy any part, use, or access CivicLens without ATI's permission.</LI>
        <LI>
          The user may not reuse or copy any part of the CSS, HTML, or other visual design features of CivicLens without
          ATI's permission.
        </LI>
        <LI>ATI may delete or remove Content posted to CivicLens that violates these Terms or for any other reason.</LI>
        <LI>
          ATI can suspend or terminate a user account and content if it determines that the account or content violates
          these Terms, applicable laws and regulations, or another party's intellectual property or other rights.
        </LI>
        <LI>
          ATI will terminate the account of any user who engages in the written, verbal, physical or other kind of abuse
          of an ATI employee or another CivicLens user.
        </LI>
        <LI>
          The user understands that the technical transmission and processing operations of CivicLens, including of user
          Content, may involve changes to conform with the technical requirements of devices or connecting networks and
          could be transmitted over a variety of networks, unencrypted.
        </LI>
        <LI>
          If the user's usage is significantly larger than the average usage of CivicLens users, then ATI maintains the
          right to suspend the user's account. In such a case, ATI will reach out to the user before further action is
          taken except in instances where the user's usage negatively impacts how ATI functions.
        </LI>
        <LI>
          ATI does not promise or warrant that (i) it will meet the user's individual requirements or expectations; (ii)
          it will be without error, timely, uninterrupted, or secure; (iii) the errors will be fixed; (iv) the results
          acquired from CivicLens will be reliable or accurate.
        </LI>
        <LI>
          The user understands that ATI will not be responsible for any indirect or direct loss or damage that may result
          from use of CivicLens.
        </LI>
        <LI>
          If ATI adds any features to enhance the service, then the features will also be dependent on these Terms. The
          user's use of CivicLens after modifications such as these will constitute the user's agreement to the
          modifications.
        </LI>
      </List>
      <P>
        <span style={{ display: 'block', marginTop: 20 }}>
          Any questions about these Terms can be sent to <A href="mailto:help@civiclens.dev">help@civiclens.dev</A>
        </span>
      </P>
      <P>
        Any claims arising out of or relating to these Terms will be governed by the laws of Washington DC laws. Any
        dispute arising from these Terms shall be resolved in the jurisdiction of the Washington DC's courts.
      </P>
    </Section>
  </>
)

const PrivacyContent = () => (
  <>
    <Title>CivicLens Privacy Policy</Title>
    <Updated>Last updated: May 13, 2021</Updated>

    <Section first title="Introduction">
      <P>
        CivicLens is a tool created by the Algorithmic Transparency Institute (“ATI”, “we”, “us”, “our”), a project of
        the <A href="https://ncoc.org/privacy-policy-terms-of-use/" external>National Conference on Citizenship</A>.
        CivicLens provides a collaborative platform for collecting, monitoring, analyzing, and responding to problematic
        content online.
      </P>
      <P>
        CivicLens allows users to log instances of problematic content posted online such as mis- and dis-information
        (“Content”), create an archive of these instances and undertake research, analysis and monitoring of trends in
        their spread. CivicLens is provided for the purposes of public interest archiving, research and/or journalism
        (“Specified Purposes”) only.
      </P>
      <P>
        This Privacy Policy (“Policy”) addresses the personal data processing undertaken through CivicLens and the Civic
        Lens website. This Policy does not address our other data processing operations and does not apply to software or
        third party applications that CivicLens integrates. This Policy should be read in conjunction with our{' '}
        <A href="/terms">Terms of Use</A>.
      </P>
      <P>
        This Policy is subject to regular review. Subsequent updates, including any that may be required by law, will be
        documented below and communicated to data subjects where required.
      </P>
    </Section>

    <Section title="Data controller">
      <P lead>ATI is the data controller for data processed in connection with this Policy. Our details are:</P>
      <Addr>
        National Conference on Citizenship
        <br />
        1920 L Street, NW
        <br />
        Suite 450
        <br />
        Washington, DC 20036
      </Addr>
      <P>
        Questions or enquiries about this policy can be directed to{' '}
        <A href="mailto:support@civiclens.dev">support@civiclens.dev</A>.
      </P>
    </Section>

    <Section title="What we process and why">
      <P>
        When users create an account with us, we collect your name and email address in order to set up and administrate
        your account. We may also use your details to send users account-related emails from time to time.
      </P>
      <P>
        When you make a submission to us or another organization operating a CivicLens Tipline (SMS, email or other
        submission links where Content can be submitted by members of the public) and provide your name or contact
        information, CivicLens may use this information to validate your submission and/or contact you to follow up on
        the submission.
      </P>
      <P>
        We may process your personal data to the extent that it is included in any instances of problematic content
        posted to CivicLens.
      </P>
      <P>
        As an account holder, you can also choose to grant us access to your social media and messaging accounts,
        allowing CivicLens to monitor the content of public channels and accounts. Enabling this feature will only allow
        CivicLens to access public content through these accounts and does not allow CivicLens to access your messages,
        contacts or any other information connected to your account. Enabling this feature is optional.
      </P>
      <P>
        When you visit our website, we use strictly necessary cookies only in order to manage traffic and keep the
        website secure. Further information about this is provided in the Cookies section below.
      </P>
      <P>
        To the extent that a legal basis needs to be identified under applicable local, national and/or international
        data protection laws (“Data Protection Laws”), we process your personal data either on the basis of your consent
        or on the basis of our legitimate interests in operating CivicLens as a mis- and dis-information archive for the
        Specified Purposes.
      </P>
    </Section>

    <Section title="Data retention">
      <P>
        In general, we retain personal data for as long as is required to fulfill the purpose it was collected for. After
        that time, we delete or irreversibly anonymize personal data.
      </P>
      <P>
        Where personal data is collected for the Specified Purposes it may be subject to exemptions and derogations
        related to research, archiving and journalism under Data Protection Laws that allow it to be retained
        indefinitely. Safeguards including data minimization and limiting access to the personal data are instituted
        accordingly.
      </P>
    </Section>

    <Section title="Sensitive data">
      <P>
        We may process sensitive or “special category” data as defined under Data Protection Laws. Typically this would
        be limited to political opinions or philosophical beliefs. We only process this type of data in the context of
        submissions of Content made to CivicLens and on the basis that this had been manifestly made public by the data
        subject who posted the instance.
      </P>
    </Section>

    <Section title="Opting in and out of cookies">
      <P>
        The cookies we use on our website are all strictly necessary in order run and secure the website and therefore
        cannot be opted in or out of.
      </P>
    </Section>

    <Section title="Information security">
      <P>
        We take steps to ensure that the personal data we process is kept secure and confidential and treated in
        accordance with this Policy and our <A href="/terms">Terms of Use</A>. We implement technical and organizational
        controls to prevent unauthorized access to personal data including limiting access to account and submitter
        personal data to individuals responsible for administrating accounts and submissions, minimizing personal data to
        what is necessary for our purposes, encrypting data where possible and instituting security protocols and staff
        training. Through our <A href="/terms">Terms of Use</A> and monitoring and vetting processes, we limit access,
        use, submissions and uploads of Content to CivicLens strictly in line with the Specified Purposes. While we make
        efforts to protect personal data, information transmitted over the internet remains vulnerable to interception
        and for this reason, the transmission of any personal data to our website, via email to us or to CivicLens is
        therefore at the data subjects' own risk.
      </P>
    </Section>

    <Section title="Data sharing and subprocessors">
      <P>
        In administrating our website and CivicLens, we engage certain third parties for web hosting, email management
        and other purposes including AWS, Heroku, Twillio, SendGrid, New Relic, and Bonsai. These third parties are
        engaged on terms which ensure confidentiality and compliance with applicable Data Protection Laws. A list of the
        processors we engage is available upon request.
      </P>
      <P>
        From time to time we may share Content uploaded or submitted to CivicLens with research partners and other
        bodies, strictly for the Specified Purposes. To the extent required under Data Protection Laws, these
        relationships are supported by agreements that ensure these third parties comply with the obligations on them
        under Data Protection Laws.
      </P>
    </Section>

    <Section title="International transfers of data">
      <P>
        Where we transfer data subject to applicable Data Protection Laws outside the EU or EEA, we will ensure that it
        is appropriately protected by entering into a transfer support mechanism with the recipient, for example the
        European Commission's Standard Contractual Clauses.
      </P>
    </Section>

    <Section title="Third party platforms and websites">
      <P>
        Our website includes links to external platforms (Facebook, Twitter and Instagram, Google and YouTube). We are
        not in control of the way these platforms process your personal data, so we recommend reviewing their respective
        privacy and cookie policies.
      </P>
    </Section>

    <Section title="Your rights">
      <P>
        We will comply with your requests to exercise your rights in relation to your personal data to the extent
        possible and required under Data Protection Laws. These rights may include the right to request access to,
        rectification or erasure of your data, object to processing, or withdraw your consent, limit the processing of
        your data and the receive the data you have provided to us.
      </P>
      <P>
        You may also have the right to bring concerns to your national data protection supervisory authority if you feel
        that your personal data has been unlawfully processed. Data subjects based in the EU or UK may also lodge
        complaints with the supervisory authority in their jurisdiction. Relevant supervisory authority contact details
        are listed{' '}
        <A href="https://ec.europa.eu/newsroom/article29/item-detail.cfm?item_id=612080" external>here</A>.
      </P>
      <P>
        Please note these rights may only be available to certain data subjects, and only to the extent that any
        exemption or derogation does not apply to the relevant data processing activities.
      </P>
    </Section>
  </>
)

const LegalPage = ({ doc = 'terms', membershipPath = '/membership', signInPath = '/sign-in', signUpPath = '/sign-up', currentUser }) => {
  return (
    <div style={{ backgroundColor: 'white' }}>
      <Header
        membershipPath={membershipPath}
        signInPath={signInPath}
        signUpPath={signUpPath}
        currentUser={currentUser}
      />
      <main
        style={{
          maxWidth: 768,
          margin: '0 auto',
          padding: '56px 24px 96px',
          color: C.gray9,
          fontFamily: 'var(--brand-body-fontFamily)',
        }}
      >
        {doc === 'privacy' ? <PrivacyContent /> : <TermsContent />}
      </main>
      <Footer />
    </div>
  )
}

export default LegalPage
